// src/components/shared/StoriesBar.tsx
// Horizontal scrolling story bubbles at top of home feed
// Shows stories from people the current user follows

import { useState, useEffect } from 'react'
import { Plus } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import StoryViewer from './StoryViewer'
import CreateStory from './CreateStory'
import { cachedUrl } from '../../lib/storageUrl'

interface Story {
  id: string
  user_id: string
  photo_url: string | null
  photo_urls?: string[] | null
  caption: string | null
  story_type: string
  created_at: string
  expires_at: string
  view_count?: number
  profiles: {
    username: string
    avatar_url: string | null
  }
  viewed: boolean
}

interface StoryGroup {
  user_id: string
  username: string
  avatar_url: string | null
  stories: Story[]
  hasUnviewed: boolean
}

interface Props {
  mode?: 'people' | 'shops'
  onShopSelect?: (shop: any) => void
}

export default function StoriesBar({ mode = 'people', onShopSelect }: Props) {
  const { profile } = useAuth()
  const [groups, setGroups] = useState<StoryGroup[]>([])
  const [myStories, setMyStories] = useState<Story[]>([])
  const [viewingGroup, setViewingGroup] = useState<StoryGroup | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [loading, setLoading] = useState(true)
  const [followedShops, setFollowedShops] = useState<any[]>([])
  const [shopStoryGroups, setShopStoryGroups] = useState<Record<string, StoryGroup>>({})

  useEffect(() => {
    if (!profile) return
    if (mode === 'shops') loadShops()
    else loadStories()
  }, [profile, mode])

  async function loadShops() {
    if (!profile) return
    setLoading(true)
    const { data: shopFollows } = await supabase
      .from('shop_follows')
      .select('shop_id, coffee_shops(id, name, photo_url, city, state)')
      .eq('user_id', profile.id)
      .limit(20)
    const shops = shopFollows?.length
      ? shopFollows.map((sf: any) => sf.coffee_shops).filter(Boolean)
      : (await supabase.from('coffee_shops').select('id, name, photo_url, city, state').eq('is_active', true).order('total_ratings', { ascending: false }).limit(12)).data || []
    setFollowedShops(shops)

    // Load active stories for these shops
    if (shops.length > 0) {
      const shopIds = shops.map((s: any) => s.id)
      const now = new Date().toISOString()
      const [storiesRes, viewsRes] = await Promise.all([
        supabase.from('stories')
          .select('id, user_id, shop_id, photo_url, photo_urls, caption, story_type, created_at, expires_at, view_count')
          .in('shop_id', shopIds)
          .gt('expires_at', now)
          .order('created_at', { ascending: false }),
        supabase.from('story_views').select('story_id').eq('viewer_id', profile.id),
      ])
      const viewedIds = new Set((viewsRes.data || []).map((v: any) => v.story_id))
      const groups: Record<string, StoryGroup> = {}
      for (const s of storiesRes.data || []) {
        if (!s.shop_id) continue
        const shop = shops.find((sh: any) => sh.id === s.shop_id)
        if (!groups[s.shop_id]) {
          groups[s.shop_id] = {
            user_id: s.user_id,
            username: shop?.name || 'Shop',
            avatar_url: shop?.photo_url || null,
            stories: [],
            hasUnviewed: false,
          }
        }
        const story: Story = { ...s, profiles: { username: shop?.name || 'Shop', avatar_url: shop?.photo_url || null }, viewed: viewedIds.has(s.id) }
        groups[s.shop_id].stories.push(story)
        if (!story.viewed) groups[s.shop_id].hasUnviewed = true
      }
      setShopStoryGroups(groups)
    }
    setLoading(false)
  }

  async function loadStories() {
    if (!profile) return

    // Get following IDs
    const { data: followingData } = await supabase
      .from('follows')
      .select('following_id')
      .eq('follower_id', profile.id)

    const followingIds = (followingData || []).map((f: any) => f.following_id)

    // Get active stories from followed users + own stories (separate query ensures own always loads)
    const now = new Date().toISOString()
    const [followingStoriesRes, ownStoriesRes] = await Promise.all([
      supabase
        .from('stories')
        .select('*, profiles(username, avatar_url)')
        .in('user_id', followingIds.length > 0 ? followingIds : ['00000000-0000-0000-0000-000000000000'])
        .gt('expires_at', now)
        .order('created_at', { ascending: false }),
      supabase
        .from('stories')
        .select('*, profiles(username, avatar_url)')
        .eq('user_id', profile.id)
        .gt('expires_at', now)
        .order('created_at', { ascending: false })
    ])
    // Merge and deduplicate
    const allStories = [...(ownStoriesRes.data || []), ...(followingStoriesRes.data || [])]
    const seenIds = new Set<string>()
    const storiesData = allStories.filter(s => { if (seenIds.has(s.id)) return false; seenIds.add(s.id); return true })

    if (!storiesData) { setLoading(false); return }

    // Get viewed story IDs
    const { data: viewsData } = await supabase
      .from('story_views')
      .select('story_id')
      .eq('viewer_id', profile.id)

    const viewedIds = new Set((viewsData || []).map((v: any) => v.story_id))

    // Group by user
    const groupMap: Record<string, StoryGroup> = {}
    const mine: Story[] = []

    for (const s of storiesData) {
      const story = { ...s, viewed: viewedIds.has(s.id) }
      if (s.user_id === profile.id) {
        mine.push(story)
      }
      if (!groupMap[s.user_id]) {
        groupMap[s.user_id] = {
          user_id: s.user_id,
          username: s.profiles?.username || '',
          avatar_url: s.profiles?.avatar_url || null,
          stories: [],
          hasUnviewed: false,
        }
      }
      groupMap[s.user_id].stories.push(story)
      if (!story.viewed && s.user_id !== profile.id) {
        groupMap[s.user_id].hasUnviewed = true
      }
    }

    // Own stories first, then unviewed, then viewed
    const sorted = Object.values(groupMap)
      .filter(g => g.user_id !== profile.id)
      .sort((a, b) => Number(b.hasUnviewed) - Number(a.hasUnviewed))

    setMyStories(mine)
    setGroups(sorted)
    setLoading(false)
  }

  async function markViewed(storyId: string) {
    if (!profile) return
    await supabase.from('story_views').upsert({ story_id: storyId, viewer_id: profile.id }, { onConflict: 'story_id,viewer_id', ignoreDuplicates: true })
  }

  // Guests have no stories to show (and loadStories never runs without a
  // profile), so render nothing instead of a permanent empty placeholder.
  if (!profile) return null
  if (loading) return <div className="h-24" />

  // Precompute ordered group lists for story-to-story navigation
  const shopGroupsList = Object.values(shopStoryGroups)
  const ownStoryGroup: StoryGroup | null = myStories.length > 0
    ? { user_id: profile!.id, username: profile!.username || '', avatar_url: profile!.avatar_url || null, stories: myStories, hasUnviewed: false }
    : null
  const allPeopleGroups: StoryGroup[] = [...(ownStoryGroup ? [ownStoryGroup] : []), ...groups]

  if (mode === 'shops') {
    return (
      <>
        <div className="flex gap-3 px-4 py-3 overflow-x-auto scrollbar-hide border-b border-cream-200">
          {followedShops.length === 0 ? (
            <p className="text-coffee-400 text-sm py-2">Explore shops in Discover to follow them</p>
          ) : followedShops.map(shop => {
            const storyGroup = shopStoryGroups[shop.id]
            const hasStories = !!storyGroup
            const hasUnviewed = storyGroup?.hasUnviewed
            return (
              <button
                key={shop.id}
                onClick={() => hasStories ? setViewingGroup(storyGroup) : onShopSelect?.(shop)}
                className="flex flex-col items-center gap-1.5 flex-shrink-0"
              >
                <div className={`w-14 h-14 rounded-full p-0.5 ${hasUnviewed ? 'bg-gradient-to-tr from-caramel to-amber-300' : hasStories ? 'bg-cream-300' : 'border-2 border-cream-200'}`}>
                  <div className="w-full h-full rounded-full overflow-hidden bg-cream-100">
                    {shop.photo_url
                      ? <img src={cachedUrl(shop.photo_url)} alt="" loading="lazy" className="w-full h-full object-cover" />
                      : <img src="/icon-192.png" alt="" className="w-full h-full object-cover" />}
                  </div>
                </div>
                <span className={`text-xs font-medium max-w-[56px] truncate ${hasUnviewed ? 'text-coffee-700' : 'text-coffee-500'}`}>{shop.name}</span>
              </button>
            )
          })}
        </div>
        {viewingGroup && (
          <StoryViewer
            key={viewingGroup.user_id}
            group={viewingGroup}
            onClose={() => { setViewingGroup(null); loadShops() }}
            onViewed={markViewed}
            isOwn={false}
            onNext={() => {
              const idx = shopGroupsList.findIndex(g => g.user_id === viewingGroup.user_id)
              const nextGroup = shopGroupsList[idx + 1]
              if (nextGroup) setViewingGroup(nextGroup)
              else { setViewingGroup(null); loadShops() }
            }}
          />
        )}
      </>
    )
  }

  if (groups.length === 0 && myStories.length === 0) return (
    <div className="flex gap-3 px-4 py-3 overflow-x-auto scrollbar-hide">
      <button onClick={() => setShowCreate(true)} className="flex flex-col items-center gap-1.5 flex-shrink-0">
        <div className="w-14 h-14 rounded-full border-2 border-dashed border-cream-300 flex items-center justify-center bg-cream-50">
          <Plus size={20} className="text-coffee-400" />
        </div>
        <span className="text-coffee-400 text-xs">Your story</span>
      </button>
      {showCreate && <CreateStory onClose={() => setShowCreate(false)} onCreated={loadStories} />}
    </div>
  )

  return (
    <>
      <div className="flex gap-3 px-4 py-3 overflow-x-auto scrollbar-hide">
        {/* Own story bubble */}
        <button onClick={() => myStories.length > 0 ? setViewingGroup({ user_id: profile!.id, username: profile!.username, avatar_url: profile!.avatar_url || null, stories: myStories, hasUnviewed: false }) : setShowCreate(true)}
          className="flex flex-col items-center gap-1.5 flex-shrink-0">
          <div className="relative">
            <div className={`w-14 h-14 rounded-full p-0.5 ${myStories.length > 0 ? 'bg-gradient-to-tr from-caramel to-amber-300' : 'border-2 border-dashed border-cream-300'}`}>
              <div className="w-full h-full rounded-full overflow-hidden bg-cream-100">
                {profile?.avatar_url
                  ? <img src={cachedUrl(profile.avatar_url)} alt="" loading="lazy" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center bg-caramel"><span className="text-white font-bold text-lg">{profile?.username?.[0]?.toUpperCase()}</span></div>}
              </div>
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-caramel rounded-full flex items-center justify-center border-2 border-white">
              <Plus size={10} className="text-white" />
            </div>
          </div>
          <span className="text-coffee-500 text-xs font-medium">Your story</span>
        </button>

        {/* Following stories */}
        {groups.map(group => (
          <button key={group.user_id}
            onClick={() => setViewingGroup(group)}
            className="flex flex-col items-center gap-1.5 flex-shrink-0">
            <div className={`w-14 h-14 rounded-full p-0.5 ${group.hasUnviewed ? 'bg-gradient-to-tr from-caramel to-amber-300' : 'bg-cream-200'}`}>
              <div className="w-full h-full rounded-full overflow-hidden bg-cream-100">
                {group.avatar_url
                  ? <img src={cachedUrl(group.avatar_url)} alt="" loading="lazy" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center bg-coffee-300"><span className="text-white font-bold text-lg">{group.username?.[0]?.toUpperCase()}</span></div>}
              </div>
            </div>
            <span className={`text-xs font-medium max-w-[56px] truncate ${group.hasUnviewed ? 'text-coffee-700' : 'text-coffee-400'}`}>
              {group.username}
            </span>
          </button>
        ))}
      </div>

      {viewingGroup && (
        <StoryViewer
          key={viewingGroup.user_id}
          group={viewingGroup}
          onClose={() => { setViewingGroup(null); loadStories() }}
          onViewed={markViewed}
          isOwn={viewingGroup.user_id === profile?.id}
          onNext={() => {
            const idx = allPeopleGroups.findIndex(g => g.user_id === viewingGroup.user_id)
            const nextGroup = allPeopleGroups[idx + 1]
            if (nextGroup) setViewingGroup(nextGroup)
            else { setViewingGroup(null); loadStories() }
          }}
        />
      )}
      {showCreate && <CreateStory onClose={() => setShowCreate(false)} onCreated={loadStories} />}
    </>
  )
}
